import { useEffect, useState } from 'react';
import type { LifeAreaTag } from '@contracts';
import { LifeAreaTagSelect } from '@ui/desktop/today/LifeAreaTagSelect';
import { CollapsibleCard } from './CollapsibleCard';

const REFLECTION_MAX_LENGTH = 140;

const TEXTAREA_STYLE = {
  width: '100%',
  padding: 'var(--space-2) var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 'var(--type-body-size)',
  fontFamily: 'var(--font-family)',
  minHeight: '56px',
  resize: 'none',
} as const;

/**
 * Phone Favorite thing about today (R8.11, today-phone-parity.md §4.7). No
 * separate "Edit" action — the card's own collapse/expand mechanism already
 * draws the viewing/editing line desktop's always-visible card needs a
 * dedicated affordance for (§4.7's explicit "deliberate simplification,
 * not an oversight" ruling).
 */
export function FavoriteThingCardPhone({
  favoriteMoment,
  onSave,
  isLast,
}: {
  readonly favoriteMoment: { readonly tags: readonly LifeAreaTag[]; readonly reflection: string } | null;
  readonly onSave: (input: { readonly tags: readonly LifeAreaTag[]; readonly reflection: string }) => void;
  readonly isLast: boolean;
}) {
  const [tags, setTags] = useState<readonly LifeAreaTag[]>(favoriteMoment?.tags ?? []);
  const [reflection, setReflection] = useState(favoriteMoment?.reflection ?? '');

  // Bug fix (qa-engineer sweep, reproduced live): resyncing on
  // `favoriteMoment?.reflection` alone left `tags` stale whenever two
  // dates' reflection text happened to match (most commonly both '' --
  // the common case for a tags-only save) -- navigating via the new
  // unbounded R8.13 calendar made hitting this far more likely than the
  // old ~2-week-bounded backfill list ever did. Both fields must gate the
  // resync, compared BY VALUE (not by `favoriteMoment` object/array
  // identity, which is a fresh reference on every `getDailyPage` reload
  // even for the same date and would otherwise wipe in-progress typing on
  // every unrelated sibling-card save).
  const tagsKey = (favoriteMoment?.tags ?? []).join(',');
  const reflectionKey = favoriteMoment?.reflection ?? '';
  useEffect(() => {
    setTags(favoriteMoment?.tags ?? []);
    setReflection(favoriteMoment?.reflection ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsKey, reflectionKey]);

  function toggleTag(tag: LifeAreaTag) {
    const next = tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag].slice(0, 5);
    setTags(next);
    onSave({ tags: next, reflection });
  }

  function commitReflection() {
    onSave({ tags, reflection });
  }

  const summary = reflection.trim().length > 0 ? reflection : undefined;

  return (
    <CollapsibleCard title="Favorite thing about today" summary={summary} isLast={isLast}>
      <LifeAreaTagSelect value={tags} onToggle={toggleTag} />

      <textarea
        aria-label="Favorite-thing reflection"
        value={reflection}
        onChange={(e) => setReflection(e.target.value.slice(0, REFLECTION_MAX_LENGTH))}
        onBlur={commitReflection}
        placeholder="One sentence is plenty."
        rows={2}
        style={{ ...TEXTAREA_STYLE, marginTop: 'var(--space-3)' }}
      />
      <div style={{ marginTop: 'var(--space-1)', textAlign: 'right', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
        {reflection.length} / {REFLECTION_MAX_LENGTH}
      </div>
    </CollapsibleCard>
  );
}
